import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import TablePagination from "@mui/material/TablePagination";
import { useQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";
import Box from "@mui/material/Box";
import { useStyles } from "./styles";
import {
  handleChangeRowsPerPage,
  handleCloseSnackbar,
  handleChangePage,
} from "./helpers";
import FilterComponent from "shared/molecules/Filter";
import FilterInputs from "./filter/FilterInputs";
import { GET_ALL_EXAM_PAPERS } from "utils/graphql/services/ai-evaluation/queries";
import autoGradeClient from "utils/graphql/autoGradeClient";
import { CircularProgress } from "@mui/material";
import { convertToIST } from "../common/Index";

type MuiAlertProps = {
  onClose: () => void;
  severity?: "error" | "info" | "success" | "warning" | undefined;
  children?: React.ReactNode;
};

const Alert = React.forwardRef<HTMLDivElement, MuiAlertProps>((props, ref) => {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export function ExamPaperList() {
  const classes = useStyles();
  const history = useHistory();
  const [isFilter, setIsFilter] = useState(false);
  const [title, setTitle] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTill, setCreatedTill] = useState("");
  const [updatedFrom, setUpdatedFrom] = useState("");
  const [updatedTill, setUpdatedTill] = useState("");

  const [appliedFilters, setAppliedFilters] = useState({
    title: "",
    createdFrom: "",
    createdTill: "",
    updatedFrom: "",
    updatedTill: "",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const {
    data,
    loading,
    error,
    refetch: gamesRefetch,
  } = useQuery(GET_ALL_EXAM_PAPERS, {
    variables: {
      input: {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        title: appliedFilters.title,
        createdFrom: appliedFilters.createdFrom || null,
        createdTill: appliedFilters.createdTill || null,
        updatedFrom: appliedFilters.updatedFrom || null,
        updatedTill: appliedFilters.updatedTill || null,
      },
    },
    client: autoGradeClient,
  });

  const handleApplyFilter = () => {
    const newFilters = {
      title,
      createdFrom,
      createdTill,
      updatedFrom,
      updatedTill,
    };
    setAppliedFilters(newFilters);
    gamesRefetch({
      input: {
        limit: rowsPerPage,
        offset: 0,
        title,
        createdFrom,
        createdTill,
        updatedFrom,
        updatedTill,
      },
    });
    setPage(0);
    setIsFilter(false);
  };

  const handleClearFilter = () => {
    setTitle("");
    setCreatedFrom("");
    setCreatedTill("");
    setUpdatedFrom("");
    setUpdatedTill("");
    const clearedFilters = {
      title: "",
      createdFrom: "",
      createdTill: "",
      updatedFrom: "",
      updatedTill: "",
    };
    setAppliedFilters(clearedFilters);
    gamesRefetch({
      input: {
        limit: rowsPerPage,
        offset: 0,
        title: "",
        createdFrom: "",
        createdTill: "",
        updatedFrom: "",
        updatedTill: "",
      },
    });
    setPage(0);
    setIsFilter(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    gamesRefetch({
      input: {
        limit: rowsPerPage,
        offset: newPage * rowsPerPage,
        title: appliedFilters.title,
        createdFrom: appliedFilters.createdFrom,
        createdTill: appliedFilters.createdTill,
        updatedFrom: appliedFilters.updatedFrom,
        updatedTill: appliedFilters.updatedTill,
      },
    });
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = +event.target.value;
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    gamesRefetch({
      input: {
        limit: newRowsPerPage,
        offset: 0,
        title: appliedFilters.title,
        createdFrom: appliedFilters.createdFrom,
        createdTill: appliedFilters.createdTill,
        updatedFrom: appliedFilters.updatedFrom,
        updatedTill: appliedFilters.updatedTill,
      },
    });
  };

  const examPaper = data ? data?.getExamPapers?.examPapers : [];
  const totalCount = data?.getExamPapers?.totalRecords ?? examPaper.length;

  return (
    <Grid container spacing={2} className={classes.root}>
      <Grid item xs={12}>
        <Paper elevation={3} className={classes.paper}>
          <Box display="flex" alignItems="center" justifyContent="end">
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push("/ai-evaluation/exam-paper/create")}
            >
              Create Exam
            </Button>
          </Box>
          <Grid item mt={2}>
            <Box display="flex" justifyContent="flex-end">
              <FilterComponent isFilter={isFilter} setIsFilter={setIsFilter}>
                <div>
                  <FilterInputs
                    title={title}
                    setTitle={setTitle}
                    createdFrom={createdFrom}
                    setCreatedFrom={setCreatedFrom}
                    createdTill={createdTill}
                    setCreatedTill={setCreatedTill}
                    updatedFrom={updatedFrom}
                    setUpdatedFrom={setUpdatedFrom}
                    updatedTill={updatedTill}
                    setUpdatedTill={setUpdatedTill}
                    handleApplyFilter={handleApplyFilter}
                    handleClearFilter={handleClearFilter}
                  />
                </div>
              </FilterComponent>
            </Box>
          </Grid>
        </Paper>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Updated By
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Created At
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Updated At
                  </TableCell>
                  <TableCell align="center">Add Question</TableCell>
                  <TableCell align="center">Subject</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {examPaper.map((examPaper) => (
                  <TableRow key={examPaper.id}>
                    <TableCell>{examPaper.id}</TableCell>
                    <TableCell>{examPaper.title}</TableCell>
                    <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                      {examPaper.createdByName}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                      {examPaper.updatedByName}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                      {convertToIST(examPaper.createdAt)}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                      {convertToIST(examPaper.updatedAt)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() =>
                          history.push(
                            `/ai-evaluation/exam-paper/add-questions/${examPaper.id}`
                          )
                        }
                      >
                        Question
                      </Button>
                    </TableCell>

                    <TableCell>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() =>
                          history.push(
                            `/ai-evaluation/exam-paper/view-subjects/${examPaper.id}`
                          )
                        }
                      >
                        Subject
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1} justifyContent="center">
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() =>
                            history.push(
                              `/ai-evaluation/exam-paper/view/${examPaper.id}`
                            )
                          }
                        >
                          View
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() =>
                            history.push(
                              `/ai-evaluation/exam-paper/update/${examPaper.id}`
                            )
                          }
                        >
                          Update
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => handleChangePage(e, newPage)}
              onRowsPerPageChange={(e: any) => handleChangeRowsPerPage(e)}
              rowsPerPageOptions={[]}
              component="div"
            />
          </TableContainer>
        )}
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => handleCloseSnackbar(setSnackbar, snackbar)}
      >
        <Alert
          onClose={() => handleCloseSnackbar(setSnackbar, snackbar)}
          severity={
            snackbar.severity as
              | "error"
              | "info"
              | "success"
              | "warning"
              | undefined
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default ExamPaperList;
